import React from 'react';
import { Siren, TrendingUp, TrendingDown } from 'lucide-react';
import LivePrice from './LivePrice';

/**
 * Shows coins where BOTH 15m-1h and 4h-1d analyses agree on direction.
 * Rendered only when at least one STRONG or MEDIUM alignment is present.
 * These are high-conviction "GÜÇLÜ ALARM" signals — multi-timeframe confluence
 * usually filters out false breakouts caused by short-term noise alone.
 */
export default function MultiTimeframeStrip({ rows, onSelect, tickPrices }) {
    const picks = (rows || [])
        .filter((r) => r.multi_tf && (r.multi_tf.tier === 'STRONG' || r.multi_tf.tier === 'MEDIUM'))
        .sort((a, b) => {
            // STRONG first, then by average confidence
            const tierA = a.multi_tf.tier === 'STRONG' ? 1 : 0;
            const tierB = b.multi_tf.tier === 'STRONG' ? 1 : 0;
            if (tierA !== tierB) return tierB - tierA;
            return (b.multi_tf.avg_confidence || 0) - (a.multi_tf.avg_confidence || 0);
        })
        .slice(0, 10);

    if (picks.length === 0) {
        return (
            <div
                data-testid="multi-tf-strip-empty"
                className="border border-amber-500/20 bg-gradient-to-r from-amber-950/15 via-slate-800 to-slate-800 rounded-xl px-4 py-3 flex items-center gap-3"
            >
                <Siren className="h-4 w-4 text-amber-500/70" />
                <span className="text-[11px] text-amber-300/80 uppercase tracking-wider">
                    Çoklu Zaman Dilimi Alarmı
                </span>
                <span className="text-xs text-slate-400">
                    · Şu an 15m–1h ve 4h–1d analizleri aynı yönde buluşan bir coin yok. En güvenilir alarmlar burada çıkar.
                </span>
            </div>
        );
    }

    const strongCount = picks.filter((p) => p.multi_tf.tier === 'STRONG').length;

    return (
        <div
            data-testid="multi-tf-strip"
            className="border border-amber-500/35 bg-gradient-to-r from-amber-950/25 via-slate-800 to-slate-800 rounded-xl overflow-hidden"
        >
            <div className="px-4 py-2 border-b border-amber-500/15 flex items-center gap-2">
                <Siren className="h-3.5 w-3.5 text-amber-400 animate-pulse" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-amber-200 font-semibold">
                    Çoklu Zaman Dilimi Alarmı
                </span>
                <span className="text-[10px] text-slate-400">
                    kısa (15 dk) <span className="text-amber-300">✚</span> orta (4 saat) grafikler aynı yönde onaylıyor · GÜÇLÜ = trend uyumu, RESMİ = işlem sinyali
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-[10px]">
                    {strongCount > 0 && (
                        <span className="font-mono font-bold px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40">
                            {strongCount} GÜÇLÜ
                        </span>
                    )}
                    <span className="text-slate-400">{picks.length} coin</span>
                </span>
            </div>
            <div className="flex gap-3 overflow-x-auto p-3 scrollbar-thin scrollbar-thumb-slate-600">
                {picks.map((r) => {
                    const mt = r.multi_tf;
                    const isLong = mt.action === 'LONG';
                    const isStrong = mt.tier === 'STRONG';
                    const Icon = isLong ? TrendingUp : TrendingDown;
                    const accent = isStrong
                        ? (isLong
                            ? 'border-emerald-400/60 bg-emerald-500/10 shadow-[0_0_18px_-6px_rgba(52,211,153,0.6)]'
                            : 'border-rose-500/60 bg-rose-500/10 shadow-[0_0_18px_-6px_rgba(244,63,94,0.6)]')
                        : (isLong
                            ? 'border-emerald-400/30 bg-emerald-500/5'
                            : 'border-rose-500/30 bg-rose-500/5');
                    const textColor = isLong ? 'text-emerald-300' : 'text-rose-300';
                    return (
                        <button
                            key={r.symbol}
                            onClick={() => onSelect?.(r.symbol)}
                            data-testid={`multi-tf-card-${r.symbol}`}
                            className={`group flex-shrink-0 w-[280px] text-left border-2 ${accent} rounded-lg p-3 hover:bg-slate-700/40 transition-all`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-1.5">
                                    <Icon className={`h-4 w-4 ${textColor}`} />
                                    <span className="font-mono font-semibold text-slate-50 text-base">
                                        {r.symbol.replace('USDT', '')}
                                    </span>
                                    <span className="text-[10px] text-slate-400">/USDT</span>
                                </div>
                                <span
                                    data-testid={`multi-tf-tier-${r.symbol}`}
                                    className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                        isStrong
                                            ? 'bg-rose-500/25 text-rose-100 border border-rose-400/50 animate-pulse'
                                            : 'bg-amber-500/20 text-amber-100 border border-amber-400/40'
                                    }`}
                                >
                                    {isStrong ? '🚨 GÜÇLÜ' : 'ORTA'}
                                </span>
                            </div>

                            <div className="text-[11px] text-slate-100 mb-2 leading-snug">
                                {mt.summary}
                            </div>
                            <div
                                data-testid={`multi-tf-official-${r.symbol}`}
                                className={`mb-2 text-[10px] font-semibold rounded-sm px-1.5 py-1 border ${
                                    r.recommendation?.official
                                        ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-100'
                                        : 'bg-slate-900/60 border-slate-600 text-slate-300'
                                }`}
                            >
                                {r.recommendation?.official
                                    ? '✓ RESMİ SİNYAL — işlem açılabilir'
                                    : 'RESMİ DEĞİL — sadece trend uyumu, işlem için resmi sinyali bekle'}
                            </div>

                            {/* Timeframe confidence bars */}
                            <div className="space-y-1 mb-2">
                                <TfBar label="15m–1h" value={mt.short_confidence} color={isLong ? 'emerald' : 'rose'} />
                                <TfBar label="4h–1d"  value={mt.medium_confidence} color={isLong ? 'emerald' : 'rose'} />
                            </div>

                            <div className="flex items-center justify-between border-t border-slate-700/50 pt-2 text-[10px]">
                                <div>
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Fiyat</div>
                                    <LivePrice symbol={r.symbol} tick={tickPrices?.[r.symbol]} fallbackPrice={r.price} className="text-slate-100 text-xs" />
                                </div>
                                <div className="text-right">
                                    <div className="text-slate-400 uppercase tracking-wider text-[9px]">Yön</div>
                                    <div className={`font-mono font-bold text-xs ${textColor}`}>
                                        {isLong ? 'YUKARI' : 'AŞAĞI'} · %{mt.avg_confidence}
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

const TfBar = ({ label, value, color = 'emerald' }) => {
    const pct = Math.max(0, Math.min(100, value || 0));
    const fillColor = color === 'rose' ? 'bg-rose-400' : 'bg-emerald-400';
    return (
        <div className="flex items-center gap-2 text-[10px]">
            <span className="w-14 text-slate-400 font-mono">{label}</span>
            <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div className={`h-full ${fillColor}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right font-mono text-slate-200">%{pct}</span>
        </div>
    );
};
